import { BUILDING_BLOCK_TYPES, CONTAINER_TYPES, GRID_NODE_TYPES } from './constants';
import { ContainerTypename, StackContainerLayoutTypename, ModuleTypename, AutoRowOrColumnSizeTypename, UnifiedGapTypename, SizedContentRowOrColumnSizeTypename, GridContainerLayoutTypename, PegboardContainerLayoutTypename } from '../../__generated__/chirp/com/hubspot/content/grid/layouts/rpc/GridLayoutsRpc';
import { convertFieldValueAvroWithoutTypenameToJson, isContainer, isSection } from './typenameUtils';
import { convertJsonToFieldValueAvro } from 'rpc-client-utils';

/**
 * Missing utility from our version of immer.
 * https://immerjs.github.io/immer/typescript#cast-utilities
 */
export function castDraft(value) {
  return value;
}
export const isJsonGridLayout = jsonGridLayout => {
  return jsonGridLayout.type === GRID_NODE_TYPES.ROOT;
};
export const isJsonSection = jsonSection => {
  return jsonSection.type === GRID_NODE_TYPES.SECTION;
};
export const isJsonContainer = jsonContainer => {
  return jsonContainer.type === GRID_NODE_TYPES.CONTAINER;
};
export const isJsonModule = jsonModule => {
  return jsonModule.type === GRID_NODE_TYPES.MODULE;
};
export const isRootNode = node => {
  return node.getType() === GRID_NODE_TYPES.ROOT;
};
export const isSectionNode = node => {
  return node.getType() === GRID_NODE_TYPES.SECTION;
};
export const isContainerNode = node => {
  return node.getType() === GRID_NODE_TYPES.CONTAINER;
};
export const isModuleNode = node => {
  return node.getType() === GRID_NODE_TYPES.MODULE;
};
export const concatenateId = (prefix, idCtr) => {
  return `${prefix}-id-${idCtr}`;
};
let lastGeneratedTimestampNameGlobal;
export const generateUniqueId = (prefix = '') => {
  const newTimetamp = `${new Date().valueOf()}`;
  let timestampForName = newTimetamp;

  // Prevent collisions when two names are generated within a single millisecond
  if (lastGeneratedTimestampNameGlobal && lastGeneratedTimestampNameGlobal.startsWith(timestampForName)) {
    // const [timestampPart, suffix] = lastGeneratedTimestampNameGlobal.split('-');
    const suffix = lastGeneratedTimestampNameGlobal.replace(timestampForName, '');
    if (suffix) {
      timestampForName = `${newTimetamp}${parseInt(suffix, 10) + 1}`;
    } else {
      timestampForName = `${newTimetamp}2`;
    }
  }

  // Save the last generated timestamp as a global, to prevent future collisions
  lastGeneratedTimestampNameGlobal = timestampForName;
  return `${prefix}${timestampForName}`;
};

/**
 * Merges a partial value into a path in an object. Useful for setting a value
 * in a nested object. Borrowed from [CellAndRowsTree._mergeIntoPath](https://git.hubteam.com/HubSpot/layout-dnd-lib/blob/c6c5d2a03bb7de53a8daf209b4e043a3d567c12e/layout-data-lib/static-1.10079/js/CellAndRowsTree/index.js#L217).
 *
 * @param obj - The object to merge into
 * @param partialValue - The partial value to merge
 * @param path - The path to merge into
 * @returns The merged object
 *
 * @example
 * const obj = { a: { b: { c: 1 } } };
 * const partialValue = { b: { c: 2 } };
 * const path = ['a', 'b', 'c'];
 * const mergedObj = mergeIntoPath(obj, partialValue, path);
 * // mergedObj is { a: { b: { c: 2 } } }
 * // obj is still { a: { b: { c: 1 } } }
 */
export const mergeIntoPath = (obj, partialValue, path) => {
  // If the path is just one key, we can just shallow merge the partial value
  if (path.length === 1) {
    return Object.assign({}, obj, {
      [path[0]]: partialValue[path[0]]
    });
  }

  // Otherwise, iterate down the supplied path, cloning objects as we go
  const objToReturn = Object.assign({}, obj);
  // We need to keep a separate reference to the object we're merging into
  // so we can return it at the end. currObjToMergeInfo will be the last object
  // we iterated to at the path, rather than the full object.
  let currObjToMergeInfo = objToReturn;
  for (const key of path) {
    let newSubValue;
    const currMergeInfo = currObjToMergeInfo[key];
    if (currMergeInfo != null) {
      newSubValue = Array.isArray(currMergeInfo) ? [...currMergeInfo] : Object.assign({}, currMergeInfo);
    } else {
      newSubValue = Array.isArray(currMergeInfo) ? [] : {};
    }
    currObjToMergeInfo[key] = newSubValue;
    currObjToMergeInfo = newSubValue;
  }

  // And then merge into the last object we iterated to (still newValue if path === [])
  currObjToMergeInfo = Object.assign(currObjToMergeInfo, partialValue);
  return objToReturn;
};
export const findNodeInTree = (tree, nodeId) => {
  // Casting these to T, not sure yet how to get around having generic findNode
  // function where we know exactly what type of node we're looking for
  if (tree.getRootId() === nodeId) {
    return tree.getRootNode();
  }
  if (tree.hasSection(nodeId)) {
    return tree.findSection(nodeId);
  }
  if (tree.hasContainer(nodeId)) {
    return tree.findContainer(nodeId);
  }
  if (tree.hasModule(nodeId)) {
    return tree.findModule(nodeId);
  }
  throw new Error(`No such node ${nodeId} in tree`);
};
const gridContentMapper = ({
  gridContent,
  gridContentCallback,
  parentNodeId,
  beforeNodeId,
  afterNodeId
}) => {
  const {
    node
  } = gridContentCallback({
    gridContent,
    gridContentCallback,
    parentNodeId,
    beforeNodeId,
    afterNodeId
  });
  if (isContainerNode(node) && isContainer(gridContent)) {
    for (const child of gridContent.children) {
      gridContentMapper({
        gridContent: child,
        gridContentCallback,
        parentNodeId: node.getId()
      });
    }
  }
};
const sectionMapper = ({
  section,
  sectionCallback,
  gridContentCallback
}) => {
  const {
    node: sectionNode
  } = sectionCallback({
    section
  });
  if (section.child) {
    gridContentMapper({
      gridContent: section.child,
      gridContentCallback,
      parentNodeId: sectionNode.getId()
    });
  }
};
export const getGridLayoutIterator = jsonTree => {
  return function iterator({
    sectionCallback,
    gridContentCallback
  }) {
    for (const section of jsonTree.content) {
      sectionMapper({
        section,
        sectionCallback,
        gridContentCallback
      });
    }
  };
};
export const getGridContainerIterator = ({
  subtree,
  parentNodeId,
  beforeNodeId,
  afterNodeId
}) => {
  return function iterator({
    sectionCallback,
    gridContentCallback
  }) {
    if (isSection(subtree)) {
      sectionMapper({
        section: subtree,
        sectionCallback,
        gridContentCallback
      });
    } else {
      gridContentMapper({
        gridContent: subtree,
        gridContentCallback,
        parentNodeId,
        beforeNodeId,
        afterNodeId
      });
    }
  };
};
const gridNodeIterator = ({
  gridNode,
  gridNodeCallback,
  parentNode,
  depth = 0,
  includeStartNode = true,
  iterateParents = false
}) => {
  if (includeStartNode) {
    gridNodeCallback({
      gridNode,
      gridNodeCallback,
      parentNode,
      depth
    });
  }
  if (iterateParents) {
    if (!isRootNode(gridNode) && !isModuleNode(gridNode)) {
      gridNodeIterator({
        gridNode: gridNode.getParentNode(),
        gridNodeCallback,
        parentNode: gridNode,
        depth: depth + 1,
        iterateParents
      });
    }
  } else {
    if (isRootNode(gridNode) || isSectionNode(gridNode) || isContainerNode(gridNode)) {
      for (const child of gridNode.getChildNodes()) {
        gridNodeIterator({
          gridNode: child,
          gridNodeCallback,
          parentNode: gridNode,
          depth: depth + 1,
          iterateParents
        });
      }
    }
  }
};
export const iterateFrom = (gridNode, {
  gridNodeCallback,
  includeStartNode,
  iterateParents
}) => {
  gridNodeIterator({
    gridNode,
    gridNodeCallback,
    parentNode: isRootNode(gridNode) ? undefined : gridNode.getParentNode(),
    includeStartNode,
    iterateParents
  });
};
const noop = (__1, __2) => {};
export const forEachTree = (trees = {}, callback = noop) => {
  for (const [key, tree] of Object.entries(trees)) {
    callback(tree, key);
  }
};
export const getGridLayoutDataTreeIterator = tree => ({
  includeStartNode = false,
  iterateParents,
  gridNodeCallback
}) => {
  iterateFrom(tree.getRootNode(), {
    includeStartNode,
    iterateParents,
    gridNodeCallback
  });
};
export function getContainerBreakpointLayout(node, breakpoint = 'default') {
  return Object.assign({}, node.getValue().layout.layout.default, node.getValue().layout.layout[breakpoint]);
}
export function isContainerGridAtBreakpoint(node, breakpoint = 'default') {
  const layout = getContainerBreakpointLayout(node, breakpoint);
  return layout.type === CONTAINER_TYPES.GRID || layout.__typename === GridContainerLayoutTypename;
}
export function isPegboardContainerAtBreakpoint(node, breakpoint = 'default') {
  const layout = getContainerBreakpointLayout(node, breakpoint);
  return layout.type === CONTAINER_TYPES.PEGBOARD || layout.__typename === PegboardContainerLayoutTypename;
}
export function isContainerStackAtBreakpoint(node, breakpoint = 'default') {
  const layout = getContainerBreakpointLayout(node, breakpoint);
  return layout.type === CONTAINER_TYPES.STACK || layout.__typename === StackContainerLayoutTypename;
}
export function createOneFRColumn() {
  return {
    size: {
      __typename: SizedContentRowOrColumnSizeTypename,
      type: 'SIZED',
      size: {
        value: 1,
        units: 'FRACTION'
      }
    }
  };
}
export function createOneFRRow() {
  return {
    size: {
      __typename: SizedContentRowOrColumnSizeTypename,
      type: 'SIZED',
      size: {
        value: 1,
        units: 'FRACTION'
      }
    }
  };
}
export function createAutoRow() {
  return {
    size: {
      __typename: AutoRowOrColumnSizeTypename,
      type: 'AUTO'
    }
  };
}
const defaultStackStyles = {
  breakpointStyles: {
    default: {
      backgroundLayers: [],
      gap: {
        __typename: UnifiedGapTypename,
        type: 'UNIFIED',
        size: {
          value: 8,
          units: 'PX'
        }
      },
      alignItems: 'CENTER'
    }
  }
};
export function createAutoColumn() {
  return {
    size: {
      __typename: AutoRowOrColumnSizeTypename,
      type: 'AUTO'
    }
  };
}
export function newStack(direction, styles = defaultStackStyles, children = []) {
  return {
    id: generateUniqueId('container-'),
    __typename: ContainerTypename,
    type: 'CONTAINER',
    children,
    layout: {
      layout: {
        default: {
          __typename: StackContainerLayoutTypename,
          type: 'STACK',
          direction
        }
      }
    },
    styles
  };
}
export function convertStackToGridLayout(numberOfChildren, stackDirection) {
  if (numberOfChildren === 0) {
    return {
      rows: [createAutoRow(), createAutoRow()],
      columns: [createAutoColumn(), createAutoColumn()]
    };
  }
  if (numberOfChildren > 4) {
    const gridSize = Math.ceil(Math.sqrt(numberOfChildren));
    return {
      rows: Array(gridSize).fill(null).map(() => createAutoRow()),
      columns: Array(gridSize).fill(null).map(() => createAutoColumn())
    };
  }
  if (stackDirection === 'HORIZONTAL') {
    return {
      rows: [createOneFRRow(), createOneFRRow()],
      columns: Array(numberOfChildren).fill(null).map(() => createAutoColumn())
    };
  } else {
    return {
      rows: Array(numberOfChildren).fill(null).map(() => createAutoRow()),
      columns: [createOneFRColumn(), createOneFRColumn()]
    };
  }
}
export function removeEmptyStacksFromGridChildren(children) {
  return children.filter(child => {
    if (child.__typename === ModuleTypename) {
      return true;
    }
    if (child.__typename === ContainerTypename) {
      if (child.children.length > 0) {
        return true;
      }
      const defaultLayout = child.layout.layout.default;
      if ((defaultLayout === null || defaultLayout === void 0 ? void 0 : defaultLayout.type) !== 'STACK') {
        return true;
      }
      return false;
    }
    return true;
  });
}
export function computeWrappingStackTypeFromParentNode(parentNode) {
  if (parentNode.isHorizontalStack()) {
    return BUILDING_BLOCK_TYPES.HORIZONTAL_STACK;
  } else if (parentNode.isVerticalStack()) {
    return BUILDING_BLOCK_TYPES.VERTICAL_STACK;
  }
  return BUILDING_BLOCK_TYPES.HORIZONTAL_STACK;
}
export const invertObjectKeysAndValues = obj => {
  const invertedObject = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    invertedObject[value] = key;
  }
  return invertedObject;
};
export function convertMapOfJsonToMapOfAvro(fieldOverrides) {
  if (!fieldOverrides) return {};
  return Object.fromEntries(Object.entries(fieldOverrides).map(([key, value]) => [key, convertJsonToFieldValueAvro(value)]));
}
export function convertMapOfAvroToMapOfJson(fieldOverrides) {
  if (!fieldOverrides) return {};
  return Object.fromEntries(Object.entries(fieldOverrides).map(([key, value]) => [key, convertFieldValueAvroWithoutTypenameToJson(value)]));
}
export function getGridBoundingBox({
  id,
  columnStart,
  columnsSpanned,
  rowStart,
  rowsSpanned
}) {
  return {
    id,
    columnLeft: columnStart,
    columnRight: columnStart + columnsSpanned - 1,
    rowTop: rowStart,
    rowBottom: rowStart + rowsSpanned - 1
  };
}
export function rowsOverlap(a, b) {
  return a.rowTop <= b.rowBottom && b.rowTop <= a.rowBottom;
}

/**
 * Binary searches `sorted` for the first index where `predicate` returns true.
 * Assumes `sorted` is ordered such that once `predicate` returns true it stays
 * true for all subsequent elements.
 */
export function findFirstIndex(sorted, predicate) {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (predicate(sorted[mid])) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  return lo;
}